/**
 * TeacherControlPanel — Phase 2.3 Board Mode Model
 *
 * Supports three explicit board modes:
 *   'edit'      — Teacher prepares board before class. All controls visible.
 *                 Destructive actions (delete slides), background picker, objective editing allowed.
 *   'presenter' — Teacher-facing HUD during live class. Live controls only.
 *                 No editing, no add/delete slide, no destructive actions.
 *                 Timer, noise level, slide navigation remain interactive.
 *   'display'   — Student-facing projected screen. ALL teacher controls hidden.
 *
 * Mode flow:
 *   Edit → Presenter (Go Live)
 *   Presenter → Display (Project)
 *   Display → Presenter (Escape)
 *   Presenter → Edit (Back to Edit)
 *
 * The `boardMode` prop is the single source of truth for what renders.
 * isTeacherMode is derived from boardMode, never set independently.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Rnd } from 'react-rnd';
import {
    Home, Maximize2, Minimize2, Settings, Plus,
    ChevronLeft, ChevronRight, Clock, Target,
    Volume2, MessageSquare, ListChecks, Shuffle,
    X, Play, Pause, RotateCcw, Edit3, Trash2,
    Monitor, EyeOff, Tv2, LayoutList
} from 'lucide-react';
import PinnedEssentialsBar from './PinnedEssentialsBar';
import DisplayTopStrip from './DisplayTopStrip';
import BuildBadge from './BuildBadge';
import ErrorBoundary from './ErrorBoundary';
import useBoardState from '../../hooks/useBoardState';
import { stripMarkdown, normalizeBullets } from '../../utils/richText';

// =============== WIDGET COMPONENTS (Compact & User-Friendly) ===============

// Timer Widget - Compact
const TimerWidget = ({ onRemove, isDisplayMode = false }) => {
    const [seconds, setSeconds] = useState(300);
    const [isRunning, setIsRunning] = useState(false);

    useEffect(() => {
        let interval;
        if (isRunning) {
            interval = setInterval(() => {
                setSeconds(s => Math.max(0, s - 1));
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [isRunning]);

    const formatTime = (s) => {
        const mins = Math.floor(s / 60);
        const secs = s % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    return (
        <div className="w-full h-full bg-white/95 backdrop-blur-sm rounded-xl shadow-lg p-3 flex flex-col">
            <div className={`flex justify-between items-center mb-2 ${isDisplayMode ? '' : 'cursor-move drag-handle'}`}>
                <div className="flex items-center gap-1">
                    <Clock className="text-amber-500" size={16} />
                    <span className="font-semibold text-slate-700 text-sm">Timer</span>
                </div>
                {!isDisplayMode && onRemove && (
                    <button onClick={onRemove} className="text-slate-400 hover:text-red-500 p-1">
                        <X size={14} />
                    </button>
                )}
            </div>
            <div className="text-3xl font-bold text-center text-slate-800 font-mono flex-1 flex items-center justify-center">
                {formatTime(seconds)}
            </div>
            {!isDisplayMode && (
                <div className="flex justify-center gap-1 mt-2">
                    <button
                        onClick={() => setIsRunning(!isRunning)}
                        className={`p-2 rounded-lg ${isRunning ? 'bg-red-500' : 'bg-emerald-500'} text-white`}
                    >
                        {isRunning ? <Pause size={16} /> : <Play size={16} />}
                    </button>
                    <button
                        onClick={() => { setSeconds(300); setIsRunning(false); }}
                        className="p-2 rounded-lg bg-slate-200 text-slate-600"
                    >
                        <RotateCcw size={16} />
                    </button>
                    {[1, 2, 3, 5, 10, 15].map(m => (
                        <button
                            key={m}
                            onClick={() => { setSeconds(m * 60); setIsRunning(false); }}
                            className={`px-2 py-1 text-xs rounded-lg ${seconds === m * 60 && !isRunning ? 'bg-amber-500 text-white' : 'bg-slate-100 hover:bg-amber-100 text-slate-600'}`}
                        >
                            {m}m
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

// Work Mode Widget - Compact
const WorkModeWidget = ({ onRemove, isDisplayMode = false }) => {
    const [mode, setMode] = useState('silent');

    const modes = [
        { id: 'silent', label: 'Silent', icon: '🔇', color: 'bg-purple-500' },
        { id: 'partner', label: 'Partner', icon: '👥', color: 'bg-blue-500' },
        { id: 'group', label: 'Group', icon: '👨‍👩‍👧‍👦', color: 'bg-green-500' },
        { id: 'teacher', label: 'Listen', icon: '🎤', color: 'bg-amber-500' }
    ];

    const currentMode = modes.find(m => m.id === mode);

    return (
        <div className="w-full h-full bg-white/95 backdrop-blur-sm rounded-xl shadow-lg p-3 flex flex-col">
            <div className={`flex justify-between items-center mb-2 ${isDisplayMode ? '' : 'cursor-move drag-handle'}`}>
                <span className="font-semibold text-slate-700 text-sm">Work Mode</span>
                {!isDisplayMode && onRemove && (
                    <button onClick={onRemove} className="text-slate-400 hover:text-red-500 p-1">
                        <X size={14} />
                    </button>
                )}
            </div>
            <div className={`${currentMode.color} rounded-lg p-3 text-white text-center flex-1 flex flex-col justify-center`}>
                <div className="text-2xl mb-1">{currentMode.icon}</div>
                <div className="font-bold text-sm">{currentMode.label}</div>
            </div>
            {!isDisplayMode && (
                <div className="flex gap-1 mt-2 justify-center">
                    {modes.map(m => (
                        <button
                            key={m.id}
                            onClick={() => setMode(m.id)}
                            className={`p-1.5 rounded-lg text-lg ${mode === m.id ? 'ring-2 ring-amber-400 bg-slate-100' : 'hover:bg-slate-50'}`}
                        >
                            {m.icon}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

// Agenda Widget - Compact
const AgendaWidget = ({ onRemove, agenda = [], isDisplayMode = false }) => {
    const [currentIndex, setCurrentIndex] = useState(0);

    const defaultAgenda = [
        { title: 'Bell Ringer', duration: 5 },
        { title: 'Instruction', duration: 15 },
        { title: 'Practice', duration: 10 },
        { title: 'Work Time', duration: 15 },
        { title: 'Exit Ticket', duration: 5 }
    ];

    const items = agenda.length > 0 ? agenda : defaultAgenda;

    return (
        <div className="w-full h-full bg-white/95 backdrop-blur-sm rounded-xl shadow-lg p-3 flex flex-col">
            <div className={`flex justify-between items-center mb-2 ${isDisplayMode ? '' : 'cursor-move drag-handle'}`}>
                <div className="flex items-center gap-1">
                    <ListChecks className="text-emerald-500" size={16} />
                    <span className="font-semibold text-slate-700 text-sm">Agenda</span>
                </div>
                {!isDisplayMode && onRemove && (
                    <button onClick={onRemove} className="text-slate-400 hover:text-red-500 p-1">
                        <X size={14} />
                    </button>
                )}
            </div>
            <div className="space-y-1 overflow-auto flex-1">
                {items.map((item, idx) => (
                    <div
                        key={idx}
                        onClick={isDisplayMode ? undefined : () => setCurrentIndex(idx)}
                        className={`flex items-center gap-2 p-2 rounded-lg ${isDisplayMode ? '' : 'cursor-pointer'} text-sm ${idx === currentIndex
                            ? 'bg-emerald-500 text-white'
                            : idx < currentIndex
                                ? 'bg-slate-100 text-slate-400 line-through'
                                : 'bg-slate-50 text-slate-700' + (isDisplayMode ? '' : ' hover:bg-slate-100')
                            }`}
                    >
                        <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${idx === currentIndex ? 'bg-white text-emerald-500' : 'bg-slate-200 text-slate-500'
                            }`}>
                            {idx + 1}
                        </span>
                        <span className="flex-1 font-medium truncate">{item.title}</span>
                        <span className="text-xs opacity-70">{item.duration}m</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

// Random Picker Widget - Compact
const PickerWidget = ({ onRemove, students = [], isDisplayMode = false }) => {
    const [picked, setPicked] = useState(null);
    const [spinning, setSpinning] = useState(false);

    const names = students.length > 0 ? students : [];

    const pickRandom = () => {
        setSpinning(true);
        let count = 0;
        const interval = setInterval(() => {
            setPicked(names[Math.floor(Math.random() * names.length)]);
            count++;
            if (count > 12) {
                clearInterval(interval);
                setSpinning(false);
            }
        }, 80);
    };

    return (
        <div className="w-full h-full bg-white/95 backdrop-blur-sm rounded-xl shadow-lg p-3 flex flex-col">
            <div className={`flex justify-between items-center mb-2 ${isDisplayMode ? '' : 'cursor-move drag-handle'}`}>
                <div className="flex items-center gap-1">
                    <Shuffle className="text-blue-500" size={16} />
                    <span className="font-semibold text-slate-700 text-sm">Picker</span>
                </div>
                {!isDisplayMode && onRemove && (
                    <button onClick={onRemove} className="text-slate-400 hover:text-red-500 p-1">
                        <X size={14} />
                    </button>
                )}
            </div>
            <div className={`bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg p-4 text-white text-center flex-1 flex items-center justify-center ${spinning ? 'animate-pulse' : ''}`}>
                <div className="text-2xl font-bold">{picked || (names.length > 0 ? '?' : '—')}</div>
            </div>
            {/* In Display mode: show picked name or dash — no button affordances */}
            {!isDisplayMode && (
                names.length === 0 ? (
                    <div className="mt-2 text-center text-sm text-slate-400">No students added yet</div>
                ) : (
                    <button
                        onClick={pickRandom}
                        disabled={spinning}
                        className="mt-2 w-full py-2 bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 text-white rounded-lg font-semibold text-sm"
                    >
                        {spinning ? 'Picking...' : 'Pick Random'}
                    </button>
                )
            )}
        </div>
    );
};

// Objective Widget - Compact
const ObjectiveWidget = ({ onRemove, objective = '', isDisplayMode = false }) => {
    const [text, setText] = useState(objective || 'I can demonstrate mastery of today\'s lesson.');
    const [editing, setEditing] = useState(false);
    const [prevObjective, setPrevObjective] = useState(objective);

    // Sync text when objective prop changes (pattern avoids setState in useEffect)
    if (objective !== prevObjective) {
        setPrevObjective(objective);
        if (objective && !editing) {
            setText(objective);
        }
    }

    return (
        <div className="w-full h-full bg-white/95 backdrop-blur-sm rounded-xl shadow-lg p-3 flex flex-col">
            <div className={`flex justify-between items-center mb-2 ${isDisplayMode ? '' : 'cursor-move drag-handle'}`}>
                <div className="flex items-center gap-1">
                    <Target className="text-red-500" size={16} />
                    <span className="font-semibold text-slate-700 text-sm">Objective</span>
                </div>
                {!isDisplayMode && (
                    <div className="flex gap-1">
                        <button onClick={() => setEditing(!editing)} className="text-slate-400 hover:text-blue-500 p-1">
                            <Edit3 size={14} />
                        </button>
                        {onRemove && (
                            <button onClick={onRemove} className="text-slate-400 hover:text-red-500 p-1">
                                <X size={14} />
                            </button>
                        )}
                    </div>
                )}
            </div>
            {/* Never show editing textarea in display mode */}
            {editing && !isDisplayMode ? (
                <textarea
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onBlur={() => setEditing(false)}
                    className="flex-1 p-2 border rounded-lg text-sm resize-none"
                    autoFocus
                />
            ) : (
                <div className="flex-1 bg-gradient-to-r from-red-50 to-orange-50 border-l-4 border-red-500 p-3 rounded-r-lg overflow-auto">
                    <p className="text-sm text-slate-800 font-medium">{text}</p>
                </div>
            )}
        </div>
    );
};

// Announcement Widget - Compact
const AnnouncementWidget = ({ onRemove, isDisplayMode = false }) => {
    const [text, setText] = useState('');
    const [editing, setEditing] = useState(!isDisplayMode);

    return (
        <div className="w-full h-full bg-white/95 backdrop-blur-sm rounded-xl shadow-lg p-3 flex flex-col">
            <div className={`flex justify-between items-center mb-2 ${isDisplayMode ? '' : 'cursor-move drag-handle'}`}>
                <div className="flex items-center gap-1">
                    <MessageSquare className="text-amber-500" size={16} />
                    <span className="font-semibold text-slate-700 text-sm">Message</span>
                </div>
                {!isDisplayMode && onRemove && (
                    <button onClick={onRemove} className="text-slate-400 hover:text-red-500 p-1">
                        <X size={14} />
                    </button>
                )}
            </div>
            {/* In display mode: always show read-only message view, never the editing textarea */}
            {editing && !isDisplayMode ? (
                <div className="flex-1 flex flex-col">
                    <textarea
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        placeholder="Type message..."
                        className="flex-1 p-2 border rounded-lg text-sm resize-none mb-2"
                    />
                    <button
                        onClick={() => setEditing(false)}
                        className="w-full py-2 bg-amber-500 text-white rounded-lg font-semibold text-sm"
                    >
                        Show
                    </button>
                </div>
            ) : (
                <div
                    onClick={isDisplayMode ? undefined : () => setEditing(true)}
                    className={`flex-1 bg-amber-50 border-2 border-amber-200 p-3 rounded-lg flex items-center justify-center ${isDisplayMode ? '' : 'cursor-pointer'}`}
                >
                    <p className="text-lg text-slate-800 font-medium text-center">{text || (isDisplayMode ? '' : 'Click to edit')}</p>
                </div>
            )}
        </div>
    );
};

// =============== SECTION TYPE THEMING ===============

const getSectionTheme = (title = '') => {
    const t = title.toLowerCase();
    if (t.includes('bell') || t.includes('warm') || t.includes('ringer') || t.includes('do now'))
        return { accent: '#f59e0b', badge: 'bg-amber-100 text-amber-700', border: 'border-l-amber-400', label: 'Bell Ringer' };
    if (t.includes('objective') || t.includes('goal') || t.includes('standard'))
        return { accent: '#10b981', badge: 'bg-emerald-100 text-emerald-700', border: 'border-l-emerald-400', label: 'Objectives' };
    if (t.includes('direct') || t.includes('instruction') || t.includes('mini-lesson') || t.includes('teach'))
        return { accent: '#3b82f6', badge: 'bg-blue-100 text-blue-700', border: 'border-l-blue-400', label: 'Direct Instruction' };
    if (t.includes('guided') || t.includes('practice') || t.includes('we do'))
        return { accent: '#8b5cf6', badge: 'bg-violet-100 text-violet-700', border: 'border-l-violet-400', label: 'Guided Practice' };
    if (t.includes('independent') || t.includes('work time') || t.includes('you do'))
        return { accent: '#06b6d4', badge: 'bg-cyan-100 text-cyan-700', border: 'border-l-cyan-400', label: 'Independent Work' };
    if (t.includes('discussion') || t.includes('partner') || t.includes('group talk'))
        return { accent: '#f97316', badge: 'bg-orange-100 text-orange-700', border: 'border-l-orange-400', label: 'Discussion' };
    if (t.includes('exit') || t.includes('closing') || t.includes('ticket'))
        return { accent: '#ec4899', badge: 'bg-pink-100 text-pink-700', border: 'border-l-pink-400', label: 'Exit Ticket' };
    return { accent: '#94a3b8', badge: 'bg-slate-100 text-slate-600', border: 'border-l-slate-300', label: '' };
};

// =============== FULL-DAY AGENDA DISPLAY PANEL ===============

const AgendaDisplayPanel = ({ segments = [], currentIndex = 0, isTeacherPreview = false }) => {
    const totalMinutes = segments.reduce((sum, s) => sum + (s.duration || 0), 0);
    const elapsedMinutes = segments.slice(0, currentIndex).reduce((sum, s) => sum + (s.duration || 0), 0);
    const remaining = totalMinutes - elapsedMinutes;
    const nextSeg = segments[currentIndex + 1] || null;
    return (
        <div className="flex flex-col items-center justify-center h-full w-full px-6 py-4 relative">

            {/* Teacher-preview ribbon — only visible in presenter mode, not projected to students */}
            {isTeacherPreview && (
                <div className="absolute top-3 right-4 flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 border border-white/20">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                    <span className="text-white/50 text-xs font-medium">Teacher Preview</span>
                </div>
            )}

            <div className="w-full max-w-3xl">
                {/* Header */}
                <div className="text-center mb-6">
                    <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white/10 border border-white/15 mb-3">
                        <LayoutList size={15} className="text-white/60" />
                        <span className="text-white/70 text-sm font-semibold uppercase tracking-widest">Today's Plan</span>
                    </div>
                    <div className="flex justify-center gap-5 text-white/35 text-xs">
                        {totalMinutes > 0 && <span>{totalMinutes} min total</span>}
                        {elapsedMinutes > 0 && <span>{elapsedMinutes} min elapsed</span>}
                        {remaining > 0 && remaining < totalMinutes && <span>{remaining} min remaining</span>}
                    </div>
                </div>

                {/* Agenda items */}
                <div className="space-y-1.5">
                    {segments.map((seg, idx) => {
                        const theme = getSectionTheme(seg.title);
                        const isCurrent = idx === currentIndex;
                        const isNext = idx === currentIndex + 1;
                        const isDone = idx < currentIndex;

                        return (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: idx * 0.04, duration: 0.25 }}
                                className={`flex items-center gap-4 rounded-2xl transition-all ${
                                    isCurrent
                                        ? 'px-6 py-4 bg-white/14 shadow-xl'
                                        : isNext
                                        ? 'px-5 py-3 bg-white/07 border border-white/12'
                                        : isDone
                                        ? 'px-5 py-2.5 opacity-35'
                                        : 'px-5 py-2.5 opacity-60'
                                }`}
                                style={{
                                    borderLeft: `4px solid ${isCurrent ? theme.accent : isNext ? 'rgba(255,255,255,0.18)' : 'transparent'}`,
                                }}
                            >
                                {/* Step indicator */}
                                <div
                                    className={`shrink-0 rounded-full flex items-center justify-center font-bold ${
                                        isCurrent
                                            ? 'w-10 h-10 text-base bg-white text-slate-800 shadow-md'
                                            : isNext
                                            ? 'w-8 h-8 text-sm bg-white/15 text-white/70'
                                            : isDone
                                            ? 'w-7 h-7 text-xs bg-white/08 text-white/40'
                                            : 'w-7 h-7 text-xs bg-white/08 text-white/50'
                                    }`}
                                >
                                    {isDone ? '✓' : idx + 1}
                                </div>

                                {/* Title + state badge */}
                                <div className="flex-1 min-w-0 flex items-center gap-3 flex-wrap">
                                    <span
                                        className={`font-semibold leading-snug ${
                                            isCurrent
                                                ? 'text-white'
                                                : isNext
                                                ? 'text-white/75'
                                                : isDone
                                                ? 'text-white/40 line-through decoration-white/25'
                                                : 'text-white/55'
                                        }`}
                                        style={{
                                            fontSize: isCurrent
                                                ? 'clamp(1.15rem, 2.2vw, 1.5rem)'
                                                : isNext
                                                ? 'clamp(0.9rem, 1.6vw, 1.1rem)'
                                                : 'clamp(0.8rem, 1.3vw, 0.95rem)',
                                        }}
                                    >
                                        {stripMarkdown(seg.title)}
                                    </span>

                                    {isCurrent && (
                                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-white/20 text-white uppercase tracking-wide shrink-0">
                                            <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                                            Now
                                        </span>
                                    )}
                                    {isNext && (
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-white/10 text-white/50 uppercase tracking-wide shrink-0">
                                            Up Next
                                        </span>
                                    )}
                                </div>

                                {/* Duration */}
                                {seg.duration && (
                                    <div className={`flex items-center gap-1 shrink-0 tabular-nums ${
                                        isCurrent ? 'text-white/70' : 'text-white/25'
                                    }`}>
                                        <Clock size={12} />
                                        <span className={`font-medium ${isCurrent ? 'text-sm' : 'text-xs'}`}>
                                            {seg.duration}m
                                        </span>
                                    </div>
                                )}
                            </motion.div>
                        );
                    })}
                </div>

                {/* Footer: next-up callout */}
                {nextSeg && (
                    <div className="mt-5 text-center">
                        <span className="text-white/30 text-xs">
                            After this:{' '}
                            <span className="text-white/50 font-medium">{stripMarkdown(nextSeg.title)}</span>
                            {nextSeg.duration && (
                                <span className="text-white/30"> ({nextSeg.duration} min)</span>
                            )}
                        </span>
                    </div>
                )}
            </div>
        </div>
    );
};

// =============== SLIDE PRESENTER (Center of Screen) ===============

const SlidePresenter = ({ segments = [], currentSlide, onSlideChange, onAddSlide, onDeleteSlide, onEditSlide, isDisplayMode = false }) => {
    const [editingTitle, setEditingTitle] = useState(false);
    const [editingContent, setEditingContent] = useState(false);
    const [titleDraft, setTitleDraft] = useState('');
    const [contentDraft, setContentDraft] = useState('');

    const defaultSegments = [
        { title: 'Bell Ringer', content: 'Complete the warm-up activity on your desk.', duration: 5 },
        { title: 'Learning Objective', content: "I can demonstrate understanding of today's key concept.", duration: 5 },
        { title: 'Direct Instruction', content: 'Watch and take notes as we explore the concept together.', duration: 15 },
        { title: 'Guided Practice', content: 'Work with your partner on the first set of problems.', duration: 10 },
        { title: 'Independent Work', content: 'Complete the assignment independently. Raise your hand if you need help.', duration: 15 },
        { title: 'Exit Ticket', content: 'Show what you learned — complete before you leave.', duration: 5 }
    ];

    const slides = segments.length > 0 ? segments : defaultSegments;
    const current = slides[currentSlide] || slides[0];
    const theme = getSectionTheme(current.title);

    const startEditTitle = () => {
        if (!onEditSlide || isDisplayMode) return;
        setTitleDraft(current.title || '');
        setEditingTitle(true);
    };

    const saveTitle = () => {
        if (onEditSlide && titleDraft.trim()) {
            onEditSlide(currentSlide, { ...current, title: titleDraft.trim() });
        }
        setEditingTitle(false);
    };

    const startEditContent = () => {
        if (!onEditSlide || isDisplayMode) return;
        setContentDraft(current.content || '');
        setEditingContent(true);
    };

    const saveContent = () => {
        if (onEditSlide && contentDraft.trim()) {
            onEditSlide(currentSlide, { ...current, content: contentDraft.trim() });
        }
        setEditingContent(false);
    };

    /** Defensive bullet parsing with richText normalization */
    const renderContent = (text) => {
        if (!text) return null;
        const lines = text.split('\n').filter(l => l.trim());
        const bulletPattern = /^\s*[•\-*]\s+/;
        const hasBullets = lines.some(l => bulletPattern.test(l));

        if (hasBullets) {
            const cleanBullets = normalizeBullets(text);
            return (
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, textAlign: 'left' }}>
                    {cleanBullets.map((line, idx) => (
                        <li key={idx} style={{
                            fontSize: 'clamp(1.05rem, 2.2vw, 1.6rem)',
                            lineHeight: 1.65,
                            marginBottom: '0.65rem',
                            color: '#1e293b',
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '0.6rem'
                        }}>
                            <span style={{ color: theme.accent, fontWeight: 700, marginTop: '0.15em', flexShrink: 0 }}>•</span>
                            <span>{line}</span>
                        </li>
                    ))}
                </ul>
            );
        }

        return (
            <p style={{
                fontSize: 'clamp(1.1rem, 2.5vw, 2rem)',
                lineHeight: 1.65,
                color: '#1e293b',
                fontWeight: 500,
                whiteSpace: 'pre-line',
                textAlign: 'left'
            }}>
                {stripMarkdown(text)}
            </p>
        );
    };

    const canGoPrev = currentSlide > 0;
    const canGoNext = currentSlide < slides.length - 1;

    return (
        <div className="flex items-center justify-center h-full w-full" style={{ minHeight: 0, padding: '0 1rem' }}>

            {/* ← Prev arrow — flanking left */}
            <button
                onClick={() => onSlideChange(Math.max(0, currentSlide - 1))}
                disabled={!canGoPrev}
                className="shrink-0 w-14 h-14 mr-4 rounded-full bg-white/10 hover:bg-white/25 border border-white/20 text-white disabled:opacity-20 disabled:cursor-not-allowed transition-all flex items-center justify-center shadow-lg"
                title="Previous section (←)"
            >
                <ChevronLeft size={28} />
            </button>

            {/* Main slide card */}
            <motion.div
                key={currentSlide}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.18 }}
                className={`bg-white/97 backdrop-blur-md rounded-3xl shadow-2xl flex flex-col border-l-[6px] ${theme.border}`}
                style={{
                    width: '100%',
                    maxWidth: '900px',
                    maxHeight: 'calc(100vh - 13rem)',
                    minHeight: '340px',
                    borderLeftColor: theme.accent,
                }}
            >
                {/* Card top: type badge + title */}
                <div className="px-8 pt-7 pb-0 shrink-0">
                    {/* Section type badge */}
                    <div className="flex items-center justify-between mb-3">
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${theme.badge}`}>
                            {theme.label || stripMarkdown(current.title)}
                        </span>
                        {/* Slide counter */}
                        <span className="text-slate-300 text-sm font-medium tabular-nums">
                            {currentSlide + 1} <span className="text-slate-200">/</span> {slides.length}
                        </span>
                    </div>

                    {/* Title — large and prominent */}
                    {editingTitle && !isDisplayMode ? (
                        <input
                            value={titleDraft}
                            onChange={(e) => setTitleDraft(e.target.value)}
                            onBlur={saveTitle}
                            onKeyDown={(e) => e.key === 'Enter' && saveTitle()}
                            className="w-full font-bold text-slate-800 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 outline-none focus:border-blue-400 mb-4"
                            style={{ fontSize: 'clamp(1.3rem, 2.5vw, 2rem)' }}
                            autoFocus
                        />
                    ) : (
                        <div
                            onClick={onEditSlide ? startEditTitle : undefined}
                            className={`font-bold text-slate-800 leading-tight mb-4 ${
                                onEditSlide ? 'cursor-pointer hover:text-slate-600 transition-colors group' : ''
                            }`}
                            style={{ fontSize: 'clamp(1.3rem, 2.5vw, 2rem)' }}
                            title={onEditSlide ? 'Click to edit title' : undefined}
                        >
                            {stripMarkdown(current.title)}
                            {onEditSlide && theme.label && (
                                <Edit3 size={14} className="inline ml-2 text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity" />
                            )}
                        </div>
                    )}
                </div>

                {/* Divider */}
                <div className="mx-8 mb-5 h-px bg-slate-100" />

                {/* Content area */}
                <div className="flex-1 px-8 pb-2" style={{ minHeight: 0, overflowY: 'auto' }}>
                    {editingContent && !isDisplayMode ? (
                        <textarea
                            value={contentDraft}
                            onChange={(e) => setContentDraft(e.target.value)}
                            onBlur={saveContent}
                            className="w-full text-slate-800 leading-relaxed bg-slate-50 border border-slate-200 rounded-xl p-4 outline-none focus:border-blue-400 resize-none"
                            style={{ fontSize: 'clamp(1rem, 2vw, 1.35rem)', minHeight: '120px' }}
                            rows={5}
                            autoFocus
                        />
                    ) : (
                        <div
                            onClick={onEditSlide ? startEditContent : undefined}
                            className={`pb-2 ${
                                onEditSlide ? 'cursor-pointer rounded-xl hover:bg-slate-50 transition-colors p-3 -mx-3' : ''
                            }`}
                            title={onEditSlide ? 'Click to edit content' : undefined}
                        >
                            {renderContent(current.content)}
                        </div>
                    )}
                </div>

                {/* Duration badge + add/delete controls */}
                <div className="px-8 pt-3 pb-6 shrink-0 flex items-center justify-between">
                    {current.duration ? (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold bg-slate-100 text-slate-500">
                            <Clock size={14} />
                            {current.duration} min
                        </span>
                    ) : <span />}

                    {/* Add/delete — edit mode only */}
                    {!isDisplayMode && (
                        <div className="flex items-center gap-1">
                            {onAddSlide && (
                                <button
                                    onClick={onAddSlide}
                                    className="p-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-slate-500 hover:text-slate-700 transition-colors"
                                    title="Add section"
                                >
                                    <Plus size={16} />
                                </button>
                            )}
                            {onDeleteSlide && slides.length > 1 && (
                                <button
                                    onClick={onDeleteSlide}
                                    className="p-2 bg-red-50 hover:bg-red-100 rounded-lg text-red-400 hover:text-red-600 transition-colors"
                                    title="Delete section"
                                >
                                    <Trash2 size={16} />
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </motion.div>

            {/* → Next arrow — flanking right */}
            <button
                onClick={() => onSlideChange(Math.min(slides.length - 1, currentSlide + 1))}
                disabled={!canGoNext}
                className="shrink-0 w-14 h-14 ml-4 rounded-full bg-white/10 hover:bg-white/25 border border-white/20 text-white disabled:opacity-20 disabled:cursor-not-allowed transition-all flex items-center justify-center shadow-lg"
                title="Next section (→)"
            >
                <ChevronRight size={28} />
            </button>
        </div>
    );
};

// =============== MAIN COMPONENT ===============

const TeacherControlPanel = ({
    boardData = {},
    students = [],
    lessonId = null,
    onNavigateHome,
    onDeleteSlides,
    userEmail,
    userId = null,
    versionNumber = null,
    lessonJson = null,
    // Phase 2.1: explicit mode prop. 'edit' | 'presenter' | 'display'
    // 'edit'      — full teacher controls (default, backward-compatible)
    // 'presenter' — teacher HUD during class, editing disabled
    // 'display'   — student-facing, ALL teacher UI hidden
    boardMode: boardModeProp = 'edit',
    onBoardModeChange,      // callback so ProductionDashboard can sync mode
}) => {
    // local mode state — initialised from prop, can be changed via Go Live / Exit
    const [boardMode, setBoardMode] = useState(boardModeProp);

    // Sync if parent changes mode programmatically
    useEffect(() => { setBoardMode(boardModeProp); }, [boardModeProp]);

    // Notify parent on mode change
    const changeMode = (newMode) => {
        setBoardMode(newMode);
        onBoardModeChange?.(newMode);
    };

    // Derived — isTeacherMode is no longer independently settable
    const isTeacherMode = boardMode !== 'display';
    const isDisplayMode = boardMode === 'display';
    const isEditMode = boardMode === 'edit';
    const isPresenterMode = boardMode === 'presenter';

    // Screen state
    const [currentScreen, _setCurrentScreen] = useState(0);
    const [screens, setScreens] = useState([
        { id: 1, name: 'Main', widgets: [], background: 'bg-gradient-to-br from-slate-800 to-slate-900' }
    ]);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [currentSlide, setCurrentSlide] = useState(0);
    // Agenda panel view — projects full-day plan to student display
    const [showAgendaView, setShowAgendaView] = useState(false);


    // --- Timer state lifted for DisplayTopStrip mirroring ---
    // PinnedEssentialsBar manages its own timer internally, but we need
    // to expose running state + seconds so DisplayTopStrip can mirror it.
    const [displayTimerSeconds, setDisplayTimerSeconds] = useState(
        boardMode !== 'edit' ? (boardData?.timerSeconds ?? 300) : 0
    );
    // eslint-disable-next-line no-unused-vars -- Phase 2.2 will wire this for Display timer sync
    const [displayTimerRunning, setDisplayTimerRunning] = useState(false);

    // Board state persistence hook — MUST be declared before any ref/effect
    // that reads boardState, otherwise minification produces a TDZ crash
    // ("Cannot access 'H' before initialization").
    const {
        boardState,
        updateObjective,
        updateNoiseLevel,
        updateTimerSeconds,
        updatePinnedSettings
    } = useBoardState({
        lessonId,
        versionNumber,
        userId: userId || userEmail,
        lessonJson: lessonJson || { objectives: boardData.objectives }
    });

    // Sync timer display seconds when board state loads (non-cascading — only on load)
    // Ref initialized to null — boardState may not have data yet on first render.
    const lastTimerRef = React.useRef(null);
    useEffect(() => {
        const latest = boardState?.lastTimerSeconds;
        if (latest && latest !== lastTimerRef.current) {
            lastTimerRef.current = latest;
            setDisplayTimerSeconds(latest);
        }
    }, [boardState?.lastTimerSeconds]);

    // Dynamic agenda from boardData or polling — mutable state
    const [agenda, setAgenda] = useState(boardData.agenda || []);

    // TODO: Re-enable image polling when image generation is ready
    // Image polling disabled — no image generation for now

    // Derive agenda from boardData when available, but allow local edits
    const rawAgenda = agenda;

    // Normalize agenda items to always have a title field for Now/Next display
    const effectiveAgenda = rawAgenda.map(item => ({
        ...item,
        title: item.title || item.phase || item.segment_name || item.name ||
            (item.content ? item.content.substring(0, 40) + (item.content.length > 40 ? '...' : '') : 'Untitled'),
        phase: item.phase || item.title || 'Activity',
        time: item.duration || item.time || null
    }));

    // Add a new blank slide after the current one
    const addSlide = () => {
        const newSlide = {
            title: `Slide ${agenda.length + 1}`,
            content: 'Click to edit this slide...',
            duration: 5
        };
        setAgenda(prev => [
            ...prev.slice(0, currentSlide + 1),
            newSlide,
            ...prev.slice(currentSlide + 1)
        ]);
        setCurrentSlide(currentSlide + 1);
    };

    // Delete the current slide
    const deleteSlide = () => {
        if (agenda.length <= 1) return;
        setAgenda(prev => prev.filter((_, idx) => idx !== currentSlide));
        setCurrentSlide(Math.max(0, currentSlide - 1));
    };

    // Edit a slide's title or content
    const editSlide = (index, updatedSlide) => {
        setAgenda(prev => prev.map((item, idx) => idx === index ? { ...item, ...updatedSlide } : item));
    };

    // Get current screen data
    const currentScreenData = screens[currentScreen] || screens[0];
    const currentWidgets = currentScreenData?.widgets || [];

    // Widget types available in the bottom toolbar.
    // Phase 2.5: 'presentation' retired — SlidePresenter is the canonical slide surface.
    // Remaining: timer, objective (Edit only), message.
    const widgetTypes = [
        { id: 'timer', label: 'Timer', icon: Clock, color: 'text-amber-500', defaultPos: { x: 20, y: 100 }, defaultSize: { width: 220, height: 180 } },
        { id: 'objective', label: 'Objective', icon: Target, color: 'text-red-500', defaultPos: { x: 20, y: 300 }, defaultSize: { width: 300, height: 150 } },
        { id: 'announcement', label: 'Message', icon: MessageSquare, color: 'text-amber-500', defaultPos: { x: 20, y: 470 }, defaultSize: { width: 260, height: 180 } }
    ];

    // Background presets
    const backgrounds = [
        'bg-gradient-to-br from-slate-800 to-slate-900',
        'bg-gradient-to-br from-indigo-900 to-purple-900',
        'bg-gradient-to-br from-emerald-800 to-teal-900',
        'bg-gradient-to-br from-amber-700 to-orange-800',
        'bg-gradient-to-br from-rose-800 to-pink-900',
        'bg-gradient-to-br from-cyan-800 to-blue-900'
    ];

    const [showBgPicker, setShowBgPicker] = useState(false);

    // Add widget
    const addWidget = (type) => {
        const widgetConfig = widgetTypes.find(w => w.id === type);
        if (!widgetConfig) return;
        const newWidget = {
            id: `${type}-${crypto.randomUUID()}`,
            type,
            position: { ...widgetConfig.defaultPos },
            size: { ...widgetConfig.defaultSize }
        };
        setScreens(prevScreens => {
            const updated = [...prevScreens];
            if (updated[currentScreen]) {
                updated[currentScreen] = {
                    ...updated[currentScreen],
                    widgets: [...updated[currentScreen].widgets, newWidget]
                };
            }
            return updated;
        });
    };

    // Remove widget
    const removeWidget = (widgetId) => {
        setScreens(prevScreens => {
            const updated = [...prevScreens];
            if (updated[currentScreen]) {
                updated[currentScreen] = {
                    ...updated[currentScreen],
                    widgets: updated[currentScreen].widgets.filter(w => w.id !== widgetId)
                };
            }
            return updated;
        });
    };

    // Update widget position/size
    const updateWidget = (widgetId, position, size) => {
        setScreens(prevScreens => {
            const updated = [...prevScreens];
            if (updated[currentScreen]) {
                updated[currentScreen] = {
                    ...updated[currentScreen],
                    widgets: updated[currentScreen].widgets.map(w =>
                        w.id === widgetId ? { ...w, position, size } : w
                    )
                };
            }
            return updated;
        });
    };

    // Render widget by type
    const renderWidget = (widget) => {
        // In display mode: no remove button, pass isDisplayMode for read-only rendering
        const props = {
            onRemove: isDisplayMode ? null : () => removeWidget(widget.id),
            isDisplayMode,
        };

        switch (widget.type) {
            case 'timer': return <TimerWidget {...props} />;
            case 'workmode': return <WorkModeWidget {...props} />;
            case 'agenda': return <AgendaWidget {...props} agenda={effectiveAgenda} />;
            case 'picker': return <PickerWidget {...props} students={students} />;
            case 'objective': return <ObjectiveWidget {...props} objective={boardData.objectives?.[0]?.text} />;
            case 'announcement': return <AnnouncementWidget {...props} />;
            default: return null;
        }
    };

    // Phase 2.2: multi-screen functions removed from toolbar.
    // Kept as dead code for potential Phase 2.3 re-enablement.
    // const addScreen = () => { ... };
    // const deleteScreen = () => { ... };


    // Change background
    const changeBackground = (bg) => {
        const updated = [...screens];
        updated[currentScreen].background = bg;
        setScreens(updated);
        setShowBgPicker(false);
    };

    // Toggle fullscreen
    const toggleFullscreen = () => {
        if (!document.fullscreenElement) {
            document.documentElement.requestFullscreen();
            setIsFullscreen(true);
        } else {
            document.exitFullscreen();
            setIsFullscreen(false);
        }
    };

    // === Keyboard handler: Escape exits display mode; arrows navigate slides in presenter ===
    const handleKeyboard = useCallback((e) => {
        if (e.key === 'Escape' && isDisplayMode) {
            e.preventDefault();
            changeMode('presenter');
            return;
        }
        // Arrow key slide navigation — active in Presenter and Display mode
        if ((isPresenterMode || isDisplayMode) && !e.target.matches('input, textarea')) {
            if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
                e.preventDefault();
                setCurrentSlide(s => Math.min(effectiveAgenda.length - 1, s + 1));
            } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
                e.preventDefault();
                setCurrentSlide(s => Math.max(0, s - 1));
            }
        }
    }, [isDisplayMode, isPresenterMode, effectiveAgenda.length]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        window.addEventListener('keydown', handleKeyboard);
        return () => window.removeEventListener('keydown', handleKeyboard);
    }, [handleKeyboard]);

    return (
        <div className={`h-screen w-full ${currentScreenData.background} flex flex-col relative overflow-hidden ${isPresenterMode ? 'ring-2 ring-inset ring-emerald-500/20' : ''}`}>

            {/* ===== TOP BAR ===== */}
            {/* Display mode: clean student-facing strip with no teacher controls */}
            {isDisplayMode ? (
                <div className="relative z-40">
                    <DisplayTopStrip
                        unitName={boardData.unitName || ''}
                        lessonDate={boardData.lessonDate || ''}
                        currentSegment={effectiveAgenda[currentSlide] || null}
                        nextSegment={effectiveAgenda[currentSlide + 1] || null}
                        timerSeconds={displayTimerSeconds}
                        timerInitialSeconds={boardState?.lastTimerSeconds || displayTimerSeconds}
                        timerRunning={displayTimerRunning}
                    />
                </div>
            ) : (
                /* Edit / Presenter mode: full teacher cockpit */
                <div className="relative z-40">
                    <PinnedEssentialsBar
                        contentObjective={boardState?.contentObjective || boardData.objectives?.[0]?.text || ''}
                        languageObjective={boardState?.languageObjective || ''}
                        onObjectiveChange={updateObjective}
                        lessonFlow={effectiveAgenda}
                        currentSegmentIndex={currentSlide}
                        timerSeconds={boardState?.lastTimerSeconds || 300}
                        timerPresets={boardState?.timerPresets || [60, 180, 300, 600]}
                        onTimerSecondsChange={(s) => {
                            updateTimerSeconds(s);
                            setDisplayTimerSeconds(s);
                        }}
                        noiseLevel={boardState?.noiseLevel || 'partner'}
                        onNoiseLevelChange={updateNoiseLevel}
                        pinnedSettings={boardState?.pinnedSettings}
                        onPinnedSettingsChange={updatePinnedSettings}
                        isTeacherMode={isTeacherMode}
                        onTeacherModeToggle={() => {
                            // Phase 2.3: PEB Eye toggle shortcuts to display from Edit/Presenter
                            changeMode('display');
                        }}
                        readOnlyObjectives={isPresenterMode}
                        isPresenterMode={isPresenterMode}
                    />
                </div>
            )}

            {/* ===== FLOATING NAV BUTTONS ===== */}
            {/* Hidden in display mode — students must not see Home or Settings */}
            {!isDisplayMode && (
                <>
                    <div className="absolute left-4 z-30" style={{ top: '96px' }}>
                        <button
                            onClick={onNavigateHome}
                            className="p-2 bg-white/10 hover:bg-white/20 rounded-xl text-white/70 hover:text-white transition-all"
                            title="Back to dashboard"
                        >
                            <Home size={20} />
                        </button>
                    </div>

                    {/* Right cluster: mode-specific controls */}
                    <div className="absolute right-4 z-30 flex items-center gap-2" style={{ top: '96px' }}>

                        {/* Background picker — Edit mode only */}
                        {isEditMode && (
                            <div className="relative">
                                <button
                                    onClick={() => setShowBgPicker(!showBgPicker)}
                                    className="p-2 bg-white/10 hover:bg-white/20 rounded-xl text-white/70 hover:text-white transition-all"
                                    title="Change background"
                                >
                                    <Settings size={20} />
                                </button>
                                <AnimatePresence>
                                    {showBgPicker && (
                                        <motion.div
                                            initial={{ opacity: 0, y: -10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -10 }}
                                            className="absolute top-12 right-0 bg-white rounded-xl shadow-2xl p-4 z-50"
                                        >
                                            <p className="text-sm font-medium text-slate-600 mb-2">Background</p>
                                            <div className="flex gap-2 flex-wrap">
                                                {backgrounds.map((bg, idx) => (
                                                    <button
                                                        key={idx}
                                                        onClick={() => changeBackground(bg)}
                                                        className={`w-8 h-8 rounded-lg ${bg} ${currentScreenData.background === bg ? 'ring-2 ring-white ring-offset-2' : ''}`}
                                                    />
                                                ))}
                                            </div>
                                            <button
                                                onClick={() => changeBackground('bg-gradient-to-br from-slate-800 to-slate-900')}
                                                className="mt-2 text-xs text-slate-500 hover:text-slate-700 underline"
                                            >
                                                Reset to Default
                                            </button>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        )}

                        {/* Fullscreen — always available in Edit and Presenter */}
                        <button
                            onClick={toggleFullscreen}
                            className="p-2 bg-white/10 hover:bg-white/20 rounded-xl text-white/70 hover:text-white transition-all"
                            title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
                        >
                            {isFullscreen ? <Minimize2 size={20} /> : <Maximize2 size={20} />}
                        </button>

                        {/* === Phase 2.3 Mode Buttons === */}

                        {/* Edit mode: Go Live → Presenter */}
                        {isEditMode && (
                            <button
                                onClick={() => changeMode('presenter')}
                                className="flex items-center gap-2 pl-4 pr-5 py-2 bg-emerald-500 hover:bg-emerald-400 text-white rounded-xl font-semibold text-sm shadow-lg transition-all"
                                title="Enter Presenter mode for live teaching"
                            >
                                <Tv2 size={16} />
                                <span className="flex flex-col items-start leading-tight">
                                    <span>Go Live</span>
                                    <span className="text-[10px] font-normal text-emerald-100 hidden xl:block">→ Presenter mode</span>
                                </span>
                            </button>
                        )}

                        {/* Presenter mode: Back to Edit + Project + PRESENTING badge */}
                        {isPresenterMode && (
                            <>
                                <button
                                    onClick={() => changeMode('edit')}
                                    className="flex items-center gap-2 px-3 py-2 bg-white/10 hover:bg-white/20 text-white/70 hover:text-white rounded-xl font-medium text-sm transition-all"
                                    title="Return to Edit mode"
                                >
                                    <Edit3 size={14} />
                                    Back to Edit
                                </button>
                                <button
                                    onClick={() => changeMode('display')}
                                    className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-400 text-white rounded-xl font-semibold text-sm shadow-lg transition-all"
                                    title="Project to students — switch to student-facing view"
                                >
                                    <Monitor size={16} />
                                    Project to Screen
                                </button>
                                {/* PRESENTING mode indicator */}
                                <span className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500/20 text-emerald-300 rounded-lg text-xs font-semibold uppercase tracking-wider border border-emerald-500/30 select-none">
                                    <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
                                    Live
                                </span>
                            </>
                        )}
                    </div>
                </>
            )}

            {/* Central Content Area */}
            <div className="flex-1 flex items-center justify-center z-10" style={{ minHeight: 0 }}>
                {/* Agenda view: accessible in Presenter (teacher preview) and Display (student projection) */}
                {showAgendaView && (isPresenterMode || isDisplayMode) ? (
                    <AgendaDisplayPanel
                        segments={effectiveAgenda}
                        currentIndex={currentSlide}
                        isTeacherPreview={isPresenterMode}
                    />
                ) : (
                    <SlidePresenter
                        segments={effectiveAgenda}
                        currentSlide={currentSlide}
                        onSlideChange={setCurrentSlide}
                        onAddSlide={isEditMode ? addSlide : null}
                        onDeleteSlide={isEditMode ? deleteSlide : null}
                        onEditSlide={isEditMode ? editSlide : null}
                        isDisplayMode={isDisplayMode}
                    />
                )}
            </div>

            {/* Draggable Widgets — static positioning in Display mode (no drag/resize) */}
            {currentWidgets.map(widget => (
                isDisplayMode ? (
                    <div
                        key={widget.id}
                        className="z-20"
                        style={{
                            position: 'absolute',
                            left: widget.position.x,
                            top: widget.position.y,
                            width: widget.size.width,
                            height: widget.size.height,
                        }}
                    >
                        {renderWidget(widget)}
                    </div>
                ) : (
                <Rnd
                    key={widget.id}
                    default={{
                        x: widget.position.x,
                        y: widget.position.y,
                        width: widget.size.width,
                        height: widget.size.height
                    }}
                    minWidth={150}
                    minHeight={120}
                    bounds="parent"
                    dragHandleClassName="drag-handle"
                    cancel="button, a, input, textarea, select, [data-no-drag]"
                    onDragStop={(e, d) => {
                        updateWidget(widget.id, { x: d.x, y: d.y }, widget.size);
                    }}
                    onResizeStop={(e, direction, ref, delta, position) => {
                        updateWidget(widget.id, position, {
                            width: parseInt(ref.style.width),
                            height: parseInt(ref.style.height)
                        });
                    }}
                    className="z-20"
                    style={{ position: 'absolute' }}
                >
                    {renderWidget(widget)}
                </Rnd>
                )
            ))}

            {/* ===== BOTTOM TOOLBAR ===== */}
            {/* Hidden entirely in display mode — students must never see widget controls */}
            {!isDisplayMode && (
                <div className="absolute bottom-0 left-0 right-0 p-4 flex justify-center z-30">
                    <div className="bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl px-5 py-3 flex items-center gap-3">

                        {/* Presenter mode: prominent slide navigation + agenda toggle */}
                        {isPresenterMode && (
                            <>
                                <button
                                    onClick={() => setCurrentSlide(s => Math.max(0, s - 1))}
                                    disabled={currentSlide === 0}
                                    className="flex items-center gap-1.5 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-sm font-semibold disabled:opacity-30 transition-all"
                                >
                                    <ChevronLeft size={16} /> Prev
                                </button>

                                <div className="flex flex-col items-center min-w-[3rem]">
                                    <span className="text-slate-800 font-bold text-lg tabular-nums leading-none">
                                        {currentSlide + 1}
                                    </span>
                                    <span className="text-slate-400 text-xs">of {effectiveAgenda.length}</span>
                                </div>

                                <button
                                    onClick={() => setCurrentSlide(s => Math.min(effectiveAgenda.length - 1, s + 1))}
                                    disabled={currentSlide >= effectiveAgenda.length - 1}
                                    className="flex items-center gap-1.5 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-sm font-semibold disabled:opacity-30 transition-all"
                                >
                                    Next <ChevronRight size={16} />
                                </button>

                                <div className="w-px h-8 bg-slate-200 mx-1" />

                                {/* Agenda view toggle — hero feature */}
                                <button
                                    onClick={() => setShowAgendaView(v => !v)}
                                    className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all border ${
                                        showAgendaView
                                            ? 'bg-amber-50 text-amber-700 border-amber-300 ring-1 ring-amber-200 shadow-sm'
                                            : 'bg-white border-slate-200 text-slate-600 hover:border-amber-300 hover:text-amber-600 hover:bg-amber-50'
                                    }`}
                                    title={showAgendaView ? 'Switch back to slide view' : 'Project full-day agenda to students'}
                                >
                                    <LayoutList size={16} />
                                    {showAgendaView ? 'Agenda (On)' : 'Agenda View'}
                                </button>

                                <div className="w-px h-8 bg-slate-200 mx-1" />

                                {/* Presenter widget buttons (timer + message only) */}
                                {widgetTypes.filter(w => ['timer', 'announcement'].includes(w.id)).map(widget => (
                                    <button
                                        key={widget.id}
                                        onClick={() => addWidget(widget.id)}
                                        className="p-2.5 hover:bg-slate-100 rounded-xl transition-colors group relative"
                                        title={`Add ${widget.label}`}
                                    >
                                        <widget.icon className={widget.color} size={20} />
                                        <span className="absolute -top-8 left-1/2 -translate-x-1/2 bg-slate-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                                            {widget.label}
                                        </span>
                                    </button>
                                ))}
                                {/* Keyboard hint */}
                                <span className="text-slate-300 text-xs ml-1 select-none hidden xl:block">← → keys</span>
                            </>
                        )}

                        {/* Edit mode: full widget suite + delete */}
                        {isEditMode && (
                            <>
                                {widgetTypes.map(widget => (
                                    <button
                                        key={widget.id}
                                        onClick={() => addWidget(widget.id)}
                                        className="p-3 hover:bg-slate-100 rounded-xl transition-colors group relative"
                                        title={widget.label}
                                    >
                                        <widget.icon className={widget.color} size={22} />
                                        <span className="absolute -top-8 left-1/2 -translate-x-1/2 bg-slate-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                                            {widget.label}
                                        </span>
                                    </button>
                                ))}

                                {onDeleteSlides && <div className="w-px h-8 bg-slate-200 mx-1" />}
                                {onDeleteSlides && (
                                    <button
                                        onClick={onDeleteSlides}
                                        className="p-2.5 hover:bg-red-50 rounded-xl group relative"
                                        title="Delete Slides"
                                    >
                                        <Trash2 className="text-slate-300 group-hover:text-red-500" size={20} />
                                        <span className="absolute -top-8 left-1/2 -translate-x-1/2 bg-slate-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                                            Delete Slides
                                        </span>
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* ===== BUILD BADGE ===== */}
            {/* Edit + Presenter only — never shown in Display mode */}
            {!isDisplayMode && (
                <div className="absolute bottom-3 left-4 z-30">
                    <BuildBadge />
                </div>
            )}
        </div>
    );
};

// Wrap with ErrorBoundary to prevent white screens
const TeacherControlPanelWithErrorBoundary = (props) => (
    <ErrorBoundary>
        <TeacherControlPanel {...props} />
    </ErrorBoundary>
);

export default TeacherControlPanelWithErrorBoundary;
